// Small pieces the trade screens share (Oct 28, 2026): the step rows a save shows (like Schedule build's),
// the status chip, the calendar result line, and the one-line summary the Builds board puts under a day.

import type { ReactNode } from "react";
import {
  ITEM_LABELS, WORK_LABELS, fmtQty, fmtTradeDay, itemUnit, type Trade, type TradeCalendarResult, type TradeTextResult,
} from "../../lib/trades";

export function StepRow({ ok, warn = false, label, children }: { ok: boolean; warn?: boolean; label: string; children: ReactNode }) {
  const tone = ok ? "text-emerald-700" : warn ? "text-rose-700" : "text-amber-700";
  const mark = ok ? "✓" : warn ? "✕" : "–";
  return (
    <div className="flex gap-2 items-start">
      <span className={"w-4 shrink-0 font-bold " + tone}>{mark}</span>
      <div className="min-w-0">
        <div className="font-semibold text-slate-800">{label}</div>
        <div className="text-xs text-slate-600">{children}</div>
      </div>
    </div>
  );
}

const STATUS_CHIP: Record<string, { cls: string; label: string }> = {
  pending: { cls: "bg-amber-100 text-amber-800", label: "No day yet" },
  scheduled: { cls: "bg-sky-100 text-sky-800", label: "Scheduled" },
  in_progress: { cls: "bg-indigo-100 text-indigo-800", label: "In progress" },
  complete: { cls: "bg-emerald-100 text-emerald-800", label: "Done" },
  cancelled: { cls: "bg-slate-100 text-slate-500", label: "Cancelled" },
};

export function TradeStatusChip({ t }: { t: Trade }) {
  const s = STATUS_CHIP[t.status] ?? STATUS_CHIP.pending;
  return <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span>;
}

/** "Detach (before the roof)", or Alex's words for Other. */
export function workLabel(t: Pick<Trade, "trade_type" | "details">): string {
  return t.trade_type === "other" && t.details?.other_label ? t.details.other_label : WORK_LABELS[t.trade_type];
}

/** "Gutters 210 ft · Downspouts 60 ft · 6" · Musket Brown" */
export function itemsLine(t: Pick<Trade, "items" | "details">, labels?: Record<string, string>): string {
  const parts = (t.items ?? []).map((i) => {
    if (i.key === "custom") return i.label ?? "";
    const label = labels?.[i.key] ?? ITEM_LABELS[i.key] ?? i.key;
    return i.qty ? `${label} ${fmtQty(i.qty, itemUnit(i.key))}` : label;
  }).filter(Boolean);
  if (t.details?.gutter_size) parts.push(t.details.gutter_size);
  if (t.details?.color) parts.push(t.details.color);
  return parts.join(" · ");
}

/** "Thu, Nov 5 · AM" or "No day yet" */
export function dayLine(t: Pick<Trade, "scheduled_date" | "arrival_window" | "status">): string {
  if (!t.scheduled_date) return "No day yet";
  return `${fmtTradeDay(t.scheduled_date)}${t.arrival_window ? ` · ${t.arrival_window}` : ""}`;
}

export function CalendarStepText({ c }: { c: TradeCalendarResult }) {
  const link = c.html_link ? <> <a href={c.html_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Open in Google ↗</a></> : null;
  switch (c.status) {
    case "created": return <>On Other Trades, guests invited.{link}</>;
    case "adopted": return <>Found the event already on Other Trades for that day and updated it.{link}</>;
    case "updated": return <>Event updated.{link}</>;
    case "called_off": return <>Marked CALLED OFF on Other Trades.{link}</>;
    case "deleted": return <>Taken off Other Trades; Google told the guests.</>;
    case "not_configured": return <>The Other Trades calendar isn't connected, so nothing went on Google. Add it by hand.</>;
    case "error": return <>Google refused it: {c.error ?? "unknown error"}.{c.day_url ? <> <a href={c.day_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Open that day ↗</a></> : null}</>;
    default: return <>No day yet, so nothing on the calendar.</>;
  }
}

export function calendarOk(c: TradeCalendarResult): boolean {
  return ["created", "adopted", "updated", "called_off", "deleted", "skipped"].includes(c.status);
}

export function TextStepText({ r }: { r: TradeTextResult }) {
  switch (r.status) {
    case "sent": return <>Sent{r.texts && r.texts.length > 1 ? ` (${r.texts.length} texts)` : ""}.{r.skipped?.length ? ` ${r.skipped.map((x) => x.detail).join(" ")}` : ""}</>;
    case "queued": return <>Goes out at {r.send_at ? new Date(r.send_at).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }) : "8 AM"}.</>;
    case "not_sent": return <>Not sent: {r.reason === "opted_out" ? "they replied STOP" : r.reason === "no_phone" ? "no phone on file" : r.skipped?.[0]?.detail ?? "no approved text for this one yet. Let them know yourself"}.</>;
    case "not_needed": return <>No text: they were told {r.told_date ? fmtTradeDay(r.told_date) : "the day"}, give or take a day.</>;
    case "failed": return <>Couldn't send: {r.error ?? "unknown error"}.</>;
    default: return <>Not sent.</>;
  }
}

export function textOk(r: TradeTextResult): boolean {
  return r.status === "sent" || r.status === "queued" || r.status === "not_needed";
}
